import React from 'react';
import { DimensionSectionProps, createLabelKey } from './types';
import TaxonomyLabel from './TaxonomyLabel';
import AddLabelAutocomplete from './AddLabelAutocomplete';
import { FiX } from 'react-icons/fi';
import IconWrapper from '../../../IconWrapper';

const DimensionSection: React.FC<DimensionSectionProps> = ({
  dimension,
  labels,
  color,
  incorrectLabels,
  onToggleIncorrect,
  missingLabels,
  onAddMissingLabel,
  onRemoveMissingLabel
}) => {
  // Convert dimension name to human-readable format
  const dimensionName = dimension
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  // Get header styling based on dimension color
  const getHeaderStyle = () => {
    if (color.includes('blue')) return 'bg-blue-200 text-blue-900';
    if (color.includes('purple')) return 'bg-purple-200 text-purple-900';
    if (color.includes('green')) return 'bg-green-200 text-green-900';
    if (color.includes('orange')) return 'bg-orange-200 text-orange-900';
    if (color.includes('red')) return 'bg-red-200 text-red-900';
    if (color.includes('yellow')) return 'bg-yellow-200 text-yellow-900';
    if (color.includes('indigo')) return 'bg-indigo-200 text-indigo-900';
    if (color.includes('pink')) return 'bg-pink-200 text-pink-900';
    if (color.includes('teal')) return 'bg-teal-200 text-teal-900';
    return 'bg-gray-200 text-gray-900';
  };

  return (
    <div className="mb-6">
      <div className={`px-4 py-2 rounded-t-lg ${getHeaderStyle()}`}>
        <h3 className="font-semibold">
          {dimensionName}
        </h3>
      </div>
      <div className="bg-white border border-gray-200 rounded-b-lg p-4">
        <div className="flex flex-wrap gap-2">
          {labels.length === 0 && missingLabels.filter(ml => ml.dimension === dimension).length === 0 && (
            <p className="text-sm text-gray-500 italic">No labels detected in this dimension</p>
          )}
          {labels.map((label) => {
            const labelKey = createLabelKey(label.dimension, label.category, label.label_name);
            return (
              <div key={labelKey} className="group">
                <TaxonomyLabel
                  label={label}
                  isIncorrect={incorrectLabels.has(labelKey)}
                  onToggleIncorrect={() => onToggleIncorrect(labelKey)}
                  color={color}
                />
              </div>
            );
          })}
          
          {/* Display missing labels for this dimension */}
          {missingLabels
            .filter(ml => ml.dimension === dimension)
            .map((missingLabel, index) => {
              const globalIndex = missingLabels.findIndex(
                ml => ml.dimension === missingLabel.dimension && 
                      ml.category === missingLabel.category && 
                      ml.label === missingLabel.label
              );
              return (
                <div
                  key={`missing-${index}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 ${color} rounded-full border-2 border-dashed border-current opacity-70`}
                >
                  <span className="text-sm font-medium">
                    {missingLabel.label}
                  </span>
                  <span className="text-xs opacity-75">
                    (suggested)
                  </span>
                  <button
                    onClick={() => onRemoveMissingLabel(globalIndex)}
                    className="ml-1 p-0.5 hover:bg-black/10 rounded transition-colors"
                  >
                    <IconWrapper icon={FiX} className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
        </div>
        
        {/* Add missing label autocomplete */}
        <div className="mt-3">
          <AddLabelAutocomplete
            dimension={dimension}
            existingLabels={[
              ...labels.map(l => l.label_name),
              ...missingLabels
                .filter(ml => ml.dimension === dimension)
                .map(ml => ml.label)
            ]}
            onAddLabel={onAddMissingLabel}
            color={color}
          />
        </div>
      </div>
    </div>
  );
};

export default DimensionSection;